import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/helpers/shadcn';

// A popover of company checkboxes. `selectedUids` is a Set, or null for every company;
// `onChange` gets the same. `colorMap` (uid -> colour) adds a swatch beside each name.
const CompanyMultiSelect = ({ companies, selectedUids, onChange, colorMap, align = 'end', className }) => {
  const activeUids = selectedUids ?? new Set(companies.map((c) => c.uid));

  const toggleCompany = (uid) => {
    const next = new Set(activeUids);
    if (next.has(uid)) {
      next.delete(uid);
    } else {
      next.add(uid);
    }
    onChange(next.size === companies.length ? null : next);
  };

  const allSelected = activeUids.size === companies.length;
  const noneSelected = activeUids.size === 0;

  const triggerLabel = allSelected ? 'All companies' : noneSelected ? 'No companies selected' : `${activeUids.size} of ${companies.length} companies`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn('h-8 w-56 justify-between text-xs font-normal', className)}>
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-64 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-xs font-medium text-muted-foreground">{activeUids.size} selected</span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => onChange(null)} disabled={allSelected}>
              All
            </Button>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => onChange(new Set())} disabled={noneSelected}>
              None
            </Button>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {companies.map((c) => (
            <label key={c.uid} className="flex cursor-pointer items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm hover:bg-accent">
              <Checkbox checked={activeUids.has(c.uid)} onCheckedChange={() => toggleCompany(c.uid)} />
              {colorMap && <div className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: colorMap[c.uid] }} />}
              <span className="truncate">{c.name}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CompanyMultiSelect;
